<template>
  <div :class="[rootClass, b(modifiers)]">
    <div :class="b('label')">
      <input
        v-bind="$attrs"
        ref="input"
        :class="b('field')"
        :disabled="disabled"
        :name="name"
        :value="modelValue"
        :placeholder="label"
        :aria-label="label"
        autocomplete="off"
        type="search"
        @blur="onBlur"
        @focus="onFocus"
        @input="onInput"
        @keyup.enter="onEnterKeyUp"
        @keyup.esc="onEscapeKeyUp"
        @mouseenter="hasHover = true"
        @mouseleave="hasHover = false"
      />
      <transition-group
        name="fade"
        tag="div"
      >
        <div
          v-show="showReset"
          key="active"
        >
          <slot
            v-if="$slots.activeAction"
            name="activeAction"
          ></slot>
          <button
            v-else
            :class="b('reset')"
            :disabled="disabled"
            :aria-label="activeActionLabel"
            type="reset"
            @click="onReset"
          >
            <e-icon-sprite
              :icon="icon"
              size="24"
            />
          </button>
        </div>
        <div
          v-show="!showReset"
          key="inactive"
        >
          <slot
            v-if="$slots.inactiveAction"
            name="inactiveAction"
          ></slot>
          <button
            v-else
            :class="b('submit')"
            :disabled="disabled"
            :aria-label="inactiveActionLabel"
            type="submit"
            @click="onSubmit"
          >
            <e-icon-sprite
              :icon="icon"
              size="24"
            />
          </button>
        </div>
      </transition-group>
      <e-search-suggestions
        ref="searchSuggestions"
        icon="i-electronic-instruction"
        :class="b('search-suggestions')"
        :suggestions="suggestions"
        @suggestion-clicked="onSuggestionClick"
      />
    </div>

    <div
      v-if="$slots.default"
      :class="b('notification')"
    >
      <slot></slot>
    </div>
  </div>
</template>

<script>
  import i18n from '@/setup/i18n.js';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import eSearchSuggestions from '@/elements/e-search-suggestions.vue';
  import propScale from '@/helpers/prop.scale.js';
  import formStates from '@/mixins/form-states.js';
  import themes from '@/mixins/themes.js';

  /**
   * Renders a search field.
   */
  export default {
    name: 'e-search',
    components: {
      eIconSprite,
      eSearchSuggestions,
    },

    // components: {},
    mixins: [formStates, themes],
    inheritAttrs: false,

    props: {
      /**
       * Value passed by v-model
       */
      modelValue: {
        type: String,
        default: null,
      },

      /**
       * Adds name attribute
       */
      name: {
        type: String,
        required: true,
      },

      /**
       * The label and placeholder value of the field.
       * The placeholder attribute is required to make floating labels work with native CSS.
       */
      label: {
        type: String,
        required: true,
      },

      /**
       * Option for selecting value text on focus.
       */
      selectOnFocus: {
        type: Boolean,
        default: true,
      },

      /**
       * Allows to change the height of the element.
       *
       * Supported values: `[300, 400, 500]`
       */
      height: propScale(500, [200, 300, 400, 500]),

      /**
       * Defines the icon to display.
       */
      icon: {
        type: String,
        default: 'search',
      },

      /**
       * Allows to define the label for the active action button.
       */
      activeActionLabel: {
        type: String,
        default() {
          return i18n.global.t('e-search.reset');
        },
      },

      /**
       * Allows to define the label for the inactive action button.
       */
      inactiveActionLabel: {
        type: String,
        default() {
          return i18n.global.t('e-search.submit');
        },
      },

      /**
       * List of suggestions to display.
       */
      suggestions: {
        type: Array,
        default: () => [],
      },

      /**
       * If set to true, the submit button will always be visible on mobile/tablet.
       */
      forceMobileSubmit: {
        type: Boolean,
        default: false,
      },

      /**
       * Hides the native browser control with CSS.
       *
       * Currently supported: `input[type="number"]`
       */
      noNativeControl: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['update:modelValue', 'focus', 'blur', 'enter', 'input', 'reset', 'suggestionClicked'],

    // data() {
    //   return {};
    // },
    computed: {
      /**
       * Defines state modifier classes.
       *
       * @returns  {Object}   BEM classes
       */
      modifiers() {
        const { noNativeControl, height, theme } = this;

        return {
          ...this.stateModifiers,
          type: this.$attrs.type || 'text',
          noNativeControl,
          height,
          suggestionsVisible: this.$refs?.searchSuggestions?.isVisible,
          theme,
        };
      },

      /**
       * Determines if the reset action should be shown.
       *
       * @returns {Boolean}
       */
      showReset() {
        if (this.forceMobileSubmit && !this.$viewport.isLg) {
          return false;
        }

        return !!this.modelValue;
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Emits input to parent component.
       *
       * @param {String}  event   Field input
       */
      onInput(event) {
        this.$emit('update:modelValue', event.target.value);
      },

      /**
       * Emits focus to parent and wrapper component.
       * Update "hasFocus" state.
       *
       * @param {Event} event - The DOM event.
       */
      onFocus(event) {
        this.hasFocus = true;

        if (this.selectOnFocus) {
          this.selectValue(event);
        }

        this.$emit('focus', event.target.value);
      },

      /**
       * Emits blur to parent and wrapper component.
       * Update "hasFocus" state.
       */
      onBlur() {
        this.hasFocus = false;

        this.$emit('blur');
      },

      /**
       * Emits enter key event to parent and wrapper component.
       */
      onEnterKeyUp() {
        this.$emit('enter');
      },

      /**
       * Selects the value of the input field.
       */
      selectValue() {
        if (this.modelValue) {
          // Needed to select a number value on Chrome.
          this.$refs.input.select();

          // Timeout is needed that it works on all browsers (without there are problems on Safari, Edge, iOS)
          if ('ontouchstart' in window) {
            setTimeout(() => {
              this.$refs.input.setSelectionRange(0, this.modelValue.length);
            });
          }
        }
      },

      /**
       * Handles the click event on a suggestion item.
       *
       * @param {Object} suggestion - The suggestion object of the clicked item.
       */
      onSuggestionClick(suggestion) {
        this.$emit('suggestionClicked', suggestion);
      },

      /**
       * Callback for the form reset event.
       */
      onReset() {
        this.$emit('update:modelValue', '');
        this.$emit('reset');

        // Change focus back to field after props update, so user can enter new term.
        this.$nextTick(() => {
          this.$refs.input.focus();
        });
      },

      /**
       * Callback for the form submit event.
       */
      onSubmit() {
        this.onEnterKeyUp();
      },

      /**
       * Escape key event handler.
       */
      onEscapeKeyUp() {
        const { input } = this.$refs;

        if (input) {
          input.blur();

          this.onReset();
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';

  // stylelint-disable no-descending-specificity

  .e-search {
    $this: &;
    $height: 56px;
    $height400: 48px;
    $height300: 40px;
    $height200: 34px;
    $border-width: 1px;

    &--theme-sagemax {
      &#{$this}--suggestions-visible {
        #{$this}__label {
          border-radius: 0;
        }
      }

      #{$this}__label {
        #{$this}__search-suggestions {
          border-radius: 0;
        }
      }
    }

    &__label {
      position: relative;
      padding: functions.get-theme-var('e-search-label-padding');
      border: functions.rem($border-width) solid transparent;
      border-radius: functions.get-theme-var('e-search-label-border-radius');
      background: functions.get-theme-var('e-search-label-background-color');

      #{$this}--suggestions-visible & {
        border-radius: functions.rem(20px 20px 0 0);
      }
    }

    &__field {
      width: 100%;
      height: functions.rem($height - ($border-width * 2));
      padding: 0;
      border: 0;
      background: transparent;
      color: functions.get-theme-var('e-search-field-color');
      font-family: variables.$font-family--text;
      font-size: functions.get-theme-var('e-search-field-font-size');
      transition: box-shadow variables.$transition-duration--200 ease-in-out;

      // disable iPhone styling
      -webkit-appearance: none;

      // sets proper input color for safari
      -webkit-text-fill-color: initial;

      &:focus {
        outline: 0;
      }

      #{$this}[class*='--state-']:not(#{$this}--state-default) & {
        padding-right: functions.get-theme-var('e-search-label-padding-right--not-state-default');
      }

      // remove x on in put field within IE
      &::-ms-clear {
        display: none;
        width: 0;
        height: 0;
      }

      // Hide autofill Safari icon
      // noinspection CssInvalidPseudoSelector
      &::-webkit-contacts-auto-fill-button {
        position: absolute;
        right: 0;
        visibility: hidden;
        pointer-events: none;
      }

      // The placeholder value is required, to allow CSS only handling of the floating label.
      &::-webkit-input-placeholder {
        // WebKit, Blink, Edge
        opacity: 1;
        color: functions.get-theme-var('e-search-field-placeholder-color');
      }

      &:-moz-placeholder {
        // Mozilla Firefox 4 to 18
        opacity: 1;
        color: functions.get-theme-var('e-search-field-placeholder-color');
      }

      &::placeholder {
        // Most modern browsers support this now
        opacity: 1;
        color: functions.get-theme-var('e-search-field-placeholder-color');
      }

      #{$this}--height-400 & {
        height: functions.rem($height400 - ($border-width * 2));
        font-size: functions.get-theme-var('e-search-field-font-size-height-400');

        @include mixins.media(lg) {
          font-size: functions.get-theme-var('e-search-field-font-size-height-400--lg');
        }
      }

      #{$this}--height-300 & {
        height: functions.rem($height300 - ($border-width * 2));
      }

      #{$this}--height-200 & {
        height: functions.rem($height200 - ($border-width * 2));
      }
    }

    &:focus-within,
    &--focus {
      outline: none;
    }

    // disabled
    &__field:disabled,
    &--disabled &__field,
    &--disabled &__field:hover {
      color: variables.$color-grayscale--200;

      & #{$this}__notification {
        color: variables.$color-grayscale--200;
      }
    }

    &__state-icon {
      position: absolute;
      top: 50%;
      right: functions.get-theme-var('e-search-state-icon-right');
      transform: translateY(-50%);
      pointer-events: none;
    }

    &__reset,
    &__submit {
      @extend %button-reset;

      position: absolute;
      top: 50%;
      right: functions.get-theme-var('e-search-submit-right');
      transform: translateY(-50%);
      color: functions.get-theme-var('e-search-submit-color');

      #{$this}--disabled & {
        color: variables.$color-grayscale--500;
      }

      .e-icon-sprite {
        width: functions.rem(23px);
        height: functions.rem(23px);
      }
    }

    &__search-suggestions {
      @include mixins.z-index(front);

      position: absolute;
      top: 100%;
      left: -1px;
      width: calc(100% + 2px);
      padding-bottom: functions.rem(15px);
      border-radius: functions.rem(0 0 20px 20px);
      background: functions.get-theme-var('e-search-label-background-color');
    }

    &__notification {
      padding: functions.get-theme-var('e-search-notification-padding');

      &:empty {
        display: none;
      }

      #{$this}--state-error & {
        color: variables.$color-status--danger;
      }

      #{$this}--state-warning & {
        color: functions.get-theme-var('color-status-warning');
      }

      #{$this}--state-info & {
        color: variables.$color-status--info;
      }

      #{$this}--state-success & {
        color: variables.$color-status--success;
      }
    }

    &--state-error &__label {
      border-color: variables.$color-status--danger;
    }

    &--state-warning &__label {
      border-color: functions.get-theme-var('color-status-warning');
    }

    // Vue transitions
    .fade-enter-active,
    .fade-leave-active {
      transition: all variables.$transition-duration--200 ease-in-out;
    }

    .fade-enter-from,
    .fade-leave-to {
      opacity: 0;
    }
  }
</style>
